import { Button } from '@/components/common/Button';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { RelationActionButton } from '@/components/common/RelationActionButton';
import type { SelectOption } from '@/components/common/Select';
import { Select } from '@/components/common/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/common/Table';
import { Role } from '@/domain/Role';
import type { User } from '@/domain/User';
import type { UserRoleFormValues } from '@/validation/userValidator';
import { userRoleSchema } from '@/validation/userValidator';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { toFormikValidate } from 'zod-formik-adapter';

interface EditUserRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: (User & { clinic?: { id: number; name: string } }) | null;
  onSubmit: (userId: number, role: string) => void;
  isPending?: boolean;
}

const roleOptions: Array<SelectOption> = [
  { label: 'Guest', value: Role.GUEST },
  { label: 'Customer', value: Role.CUSTOMER },
  { label: 'Doctor', value: Role.DOCTOR },
  { label: 'Packer', value: Role.PACKER },
];

export function EditUserRoleDialog({
  open,
  onOpenChange,
  user,
  onSubmit,
  isPending,
}: EditUserRoleDialogProps) {
  const formik = useFormik<UserRoleFormValues>({
    initialValues: {
      role: user?.role ?? Role.GUEST,
    },
    validate: toFormikValidate(userRoleSchema),
    enableReinitialize: true,
    onSubmit: (values) => {
      if (user) {
        onSubmit(user.id, values.role);
      }
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    }
  }, [open]);

  if (!user) return null;

  return (
    <DialogWrapper
      open={open}
      onOpenChange={onOpenChange}
      title="Change User Role"
      maxWidth="4xl"
    >
      <form onSubmit={formik.handleSubmit} className="mt-4 flex flex-col gap-6">
        <Table>
          <TableBody>
            <TableRow className="border-none hover:bg-transparent">
              <TableCell className="text-table-foreground w-24 px-0 py-2 font-semibold">
                User:
              </TableCell>
              <TableCell className="text-table-foreground px-0 py-2">
                {user.name} {user.surname}
              </TableCell>
            </TableRow>
            <TableRow className="border-none hover:bg-transparent">
              <TableCell className="text-table-foreground w-24 px-0 py-2 font-semibold">
                Email:
              </TableCell>
              <TableCell className="text-table-foreground px-0 py-2">
                {user.email}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Select
          id="role"
          name="role"
          label="Role"
          options={roleOptions}
          value={formik.values.role}
          onChange={(value) => formik.setFieldValue('role', value)}
          onBlur={formik.handleBlur}
          error={formik.errors.role}
          touched={formik.touched.role}
          placeholder="Select role"
        />

        <div className="flex items-center justify-between gap-4">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!formik.isValid || isPending}
          >
            {isPending ? 'Processing...' : 'Save Changes'}
          </Button>
          <RelationActionButton
            type="button"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </RelationActionButton>
        </div>
      </form>
    </DialogWrapper>
  );
}
